
// MARK: Api
import * as api from "@startapp/mistral-admin-api";

// MARK: Mobx
import { observable, action, computed } from "mobx";

// MARK: Components
import { IRowItem, ColumnType } from "../../components/Table/TableRow";

// MARK: Stores
import VariableChangeHandler from "../../resources/VariableChangeHandler";
import { uiStore, routerStore, languageStore } from "../_rootStore";
import ImageService from "../../services/ImageService";

// MARK: Resources
import strings from "../../resources/strings";

export default class BannerStore extends VariableChangeHandler {
	// Services
	public imageService: ImageService = new ImageService();
	public mobileImageService: ImageService = new ImageService();

	// Control
	@observable public loading: boolean = false;
	@observable public onEditMode: boolean = false;
	@observable public pageOffSet: number = 0;

	@observable public banners: api.Banner[] = [];
	@observable public selectedBanner: api.Banner | null = null;

	// Variables
	@observable public title: string = "";
	@observable public nick: string = "";
	@observable public description: string = "";
	@observable public url: string = "";

	@observable public bannerInfo: api.BannerInfo | null = null;
	@observable public bannerLanguageTranslation: api.BannerLanguageTranslation | null = null;

	@computed
	public get selectedLanguage(): api.Language {
		return languageStore.selectedLanguage;
	}

	@computed
	public get header() {
		const header = strings.banner.table.header;
		return [
			header.picture,
			header.nick,
			header.title,
			header.description,
		];
	}

	private static formatBanner(banner: api.Banner): IRowItem {
		const bannerInSelectedLanguage = banner.languages.filter((bannerLanguage) =>
			bannerLanguage.language === languageStore.selectedLanguage)[0];

			return {
				id: banner.id,
				name: bannerInSelectedLanguage.translation.title,
				columns: [
					{
						value: bannerInSelectedLanguage.translation.image.url,
						type: ColumnType.avatar,
					},
					{
						value: bannerInSelectedLanguage.translation.nick,
					},
					{
						value: bannerInSelectedLanguage.translation.title,
					},
					{
						value: bannerInSelectedLanguage.translation.description,
					},
				],
			};
	}

	@computed
	public get bannersTableRows(): IRowItem[] {
		return this.banners.map(BannerStore.formatBanner);
	}

	@action
	public getBanner = async (bannerId: string) => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			this.selectedBanner = await api.getBanner(bannerId);
			this.setEditorFields(this.selectedBanner);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public getBanners = async (pageOffSet?: number) => {
		if (this.loading) {
			return;
		}

		if (!pageOffSet) {
			pageOffSet = 0;
		}

		this.loading = true;

		try {
			this.banners = await api.getBanners(pageOffSet);
			this.pageOffSet = pageOffSet;
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public createOrEditBanner = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			if (this.imageService.singleImageUploader && this.imageService.singleImageUploader.uploadedUncertainImage) {
				if (!this.selectedBanner) {
					const newBanner: api.NewBanner = {
						title: this.title,
						nick: this.nick,
						description: this.description,
						url: this.url ? this.url : null,
						image: this.imageService.singleImageUploader.uploadedUncertainImage,
						mobileImage: (this.mobileImageService.singleImageUploader ?
							this.mobileImageService.singleImageUploader.uploadedUncertainImage :
							null
						),
					};
					this.selectedBanner = await api.createBanner(newBanner);
				} else {
					const editedBanner: api.EditBanner = {
						language: languageStore.selectedLanguage,
						nick: this.nick,
						title: this.title,
						description: this.description,
						url: this.url,
						image: this.imageService.singleImageUploader.uploadedUncertainImage,
						mobileImage: (this.mobileImageService.singleImageUploader ?
							this.mobileImageService.singleImageUploader.uploadedUncertainImage :
							null
						),
					};
					this.selectedBanner = await api.editBanner(this.selectedBanner.id, editedBanner);
				}
				routerStore.replace("/dashboard/banners");
				window.location.reload();
			} else {
				uiStore.showSnackbar(strings.banner.createOrEdit.errors.imageNotFound);
			}
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public deleteBanner = async (bannerId: string) => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			await api.deleteBanner(bannerId);
			this.banners = this.banners.filter((banner) => banner.id !== bannerId);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public openDeleteBannerDialog = async (bannerId: string, bannerName: string) => {
		uiStore.showDialog({
			title: strings.banner.deleteBannerDialog.title,
			message: `${strings.banner.deleteBannerDialog.message}: ${bannerName}`,
		}, () => this.deleteBanner(bannerId));
	};

	@action
	public nextPage = async () => {
		await this.getBanners(this.pageOffSet + 1);
	};

	@action
	public previousPage = async () => {
		if (this.pageOffSet > 0) {
			await this.getBanners(this.pageOffSet - 1);
		}
	};

	@action
	public clear = () => {
		this.imageService.clear();
		this.mobileImageService.clear();
		this.title = "";
		this.nick = "";
		this.description = "";
		this.url = "";
	};

	@action
	public setEditorFields = (banner: api.Banner) => {
		const bannerInSelectedLanguage = banner.languages.filter((bannerLanguage) =>
			bannerLanguage.language === languageStore.selectedLanguage)[0].translation;

		this.imageService.setSingleImage(bannerInSelectedLanguage.image);
		if (bannerInSelectedLanguage.mobileImage) {
			this.mobileImageService.setSingleImage(bannerInSelectedLanguage.mobileImage);
		}
		this.title = bannerInSelectedLanguage.title;
		this.nick = bannerInSelectedLanguage.nick;
		this.description = bannerInSelectedLanguage.description ? bannerInSelectedLanguage.description : "";
		this.url = bannerInSelectedLanguage.url ? bannerInSelectedLanguage.url : "";
	};

	@action
	public startBannerCreateOrEdit(bannerId?: string) {
		if (bannerId) {
			this.getBanner(bannerId);
			routerStore.push(`/dashboard/banners/editor/${bannerId}`);
		} else {
			this.selectedBanner = null;
			this.clear();
			routerStore.push("/dashboard/banners/editor/");
		}
	}

}
